<template>
  <div class="flex flex-column gap-3">
    <h3>Time</h3>
    <p class="m-0">
      Compare the scheduled, tracked, and signed times for this shift. Select a highlighted time to review its
      violation.
    </p>
    <div class="wrapper">
      <shift-time title="Scheduled" :times="scheduledTime" :highlights="scheduledHighlights"
        @authorization="openAuthorization" />
      <shift-time title="Tracked" :times="actualTime" :highlights="actualHighlights"
        @authorization="openAuthorization" />
      <shift-time title="Signed" :times="signedTime" :highlights="signedHighlights"
        @authorization="openAuthorization" />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import shiftsHelper from '@/helpers/shifts'
import util from '@/util'
import ShiftTime from '../shift-time.vue'
import AuthReasonMixin from '../../../authorizations/mixins/AuthReasonMixin.js'
import { AUTH_STATUSES } from '@/store/modules/shifts/constants'
import { getShiftTimeHighlightMetadata } from '../../utils/shift-time-highlights.js'

const { mapState: shiftState } = createNamespacedHelpers('shifts')

export default {
  components: {
    'shift-time': ShiftTime
  },
  mixins: [shiftsHelper, AuthReasonMixin],
  computed: {
    ...shiftState({
      shift: state => state.shift.shift,
      authorizations: state => state.shiftAuthorizations
    }),
    activeAuthorizationKeys() {
      return (
        this.shift.authorizations_with_status
          ?.filter(item => item.status === 'initial')
          .map(item => item.key) ?? []
      )
    },
    scheduledHighlights() {
      return this.getTimeHighlights('scheduled_')
    },
    actualHighlights() {
      return this.getTimeHighlights('actual_')
    },
    signedHighlights() {
      return this.getTimeHighlights('')
    },
    longWorkDuration() {
      return this.$store.state.applicationState.timeConstants.long_work_duration
    },
    longWorkWithWaiverDuration() {
      return this.$store.state.applicationState.timeConstants.long_work_with_waiver_duration
    },

    scheduledTime() {
      return this.generateTimeData('scheduled_')
    },

    actualTime() {
      return this.generateTimeData('actual_')
    },

    signedTime() {
      return this.generateTimeData('')
    }
  },
  methods: {
    getEmptyTimeData() {
      return {
        scheduled_start: null,
        scheduled_end: null,
        start: null,
        end: null,
        lunch_start: null,
        lunch_end: null,
        second_lunch_start: null,
        second_lunch_end: null,
        second_meal_status: null,
        work_hours: null,
        pay_hours: null,
        duration: null
      }
    },
    getPayCodeDurationMinutes(field) {
      if (this.shift.duration != null) {
        return this.shift.duration
      }

      const hours = Number(this.shift.hours?.[field])

      return Number.isFinite(hours) ? hours * 60 : null
    },
    getPayCodeSignedTimeData() {
      return {
        scheduled_start: this.shift.scheduled_start,
        scheduled_end: this.shift.scheduled_end,
        start: this.shift.scheduled_start,
        end: this.shift.scheduled_end,
        lunch_start: null,
        lunch_end: null,
        second_lunch_start: null,
        second_lunch_end: null,
        second_meal_status: null,
        work_hours: this.getPayCodeDurationMinutes('work_hours'),
        pay_hours: this.getPayCodeDurationMinutes('pay_hours'),
        duration: this.getPayCodeDurationMinutes('duration')
      }
    },
    getHighlightColor(authorization) {
      return AUTH_STATUSES[authorization]?.color ?? '#fff1f0'
    },
    getTimeHighlights(prefix) {
      if (this.shift.pay_code && prefix !== '') {
        return {}
      }

      const descriptors = [
        { key: 'start', label: 'Start' },
        { key: 'end', label: 'End' },
        { key: 'lunch_start', label: '1st Meal Break Start' },
        { key: 'lunch_end', label: '1st Meal Break End' },
        { key: 'second_lunch', label: '2nd Meal Break', highlightKeys: ['second_lunch_start', 'second_lunch_end'] },
        { key: 'second_lunch_start', label: '2nd Meal Break Start' },
        { key: 'second_lunch_end', label: '2nd Meal Break End' },
        { key: 'work_hours', label: 'Work Hours' }
      ]

      return descriptors.reduce((highlights, row) => {
        highlights[row.key] = getShiftTimeHighlightMetadata({
          row,
          prefix,
          authorizationKeys: this.activeAuthorizationKeys,
          shift: this.shift,
          authStatuses: AUTH_STATUSES,
          authRules: this.AUTH_RULES,
          resolveColor: this.getHighlightColor,
          discrepancyThreshold: this.$store.state.applicationState.timeConstants.signed_actual_time_discrepancy
        })
        return highlights
      }, {})
    },
    openAuthorization(authorizationType) {
      const authorization = this.authorizations.find(
        item => item.type === authorizationType && item.status === 'initial' && !item.deleted_at
      )

      if (authorization) {
        this.$emit('open-authorization', authorization.id)
      }
    },
    isSecondLunchWaiveAvailable(start, end, firstLunchStart, firstLunchEnd) {
      if (!start || !end || !firstLunchStart || !firstLunchEnd) {
        return false
      }

      const duration = util.countDuration(start, end, 'minutes')
      const lunchDuration = util.countDuration(firstLunchStart, firstLunchEnd, 'minutes')

      return (
        duration - lunchDuration >= this.longWorkDuration && duration - lunchDuration < this.longWorkWithWaiverDuration
      )
    },
    getSecondMealBreakStatus(prefix, workStart, workEnd, lunchStart, lunchEnd, secondLunchStart, secondLunchEnd) {
      if (prefix === 'scheduled_' || secondLunchStart || secondLunchEnd) {
        return null
      }

      const isWaived =
        this.shift.lunch_waived && this.isSecondLunchWaiveAvailable(workStart, workEnd, lunchStart, lunchEnd)

      if (isWaived) {
        return 'waived'
      }

      const isMissed =
        this.shift.authorizations_with_status?.some(
          item => item.status === 'initial' && item.key === 'no_second_lunch_authorization'
        ) ?? false

      return isMissed ? 'missed' : null
    },
    generateTimeData(prefix) {
      if (this.shift.pay_code && prefix !== '') {
        return this.getEmptyTimeData()
      }

      if (this.shift.pay_code) {
        return this.getPayCodeSignedTimeData()
      }

      const start = prefix === 'scheduled_' ? this.shift[`${prefix}start`] : this.shift[`${prefix}work_start`]
      const end = prefix === 'scheduled_' ? this.shift[`${prefix}end`] : this.shift[`${prefix}work_end`]
      const lunchStart = this.shift[`${prefix}lunch_start`] || null
      const lunchEnd = this.shift[`${prefix}lunch_end`] || null
      const secondLunchStart = this.shift[`${prefix}second_lunch_start`] || null
      const secondLunchEnd = this.shift[`${prefix}second_lunch_end`] || null

      return {
        scheduled_start: this.shift.scheduled_start,
        scheduled_end: this.shift.scheduled_end,
        start,
        end,
        lunch_start: lunchStart,
        lunch_end: lunchEnd,
        second_lunch_start: secondLunchStart,
        second_lunch_end: secondLunchEnd,
        second_meal_status: this.getSecondMealBreakStatus(
          prefix,
          start,
          end,
          lunchStart,
          lunchEnd,
          secondLunchStart,
          secondLunchEnd
        ),
        work_hours: this.shift.hours[`${prefix}work_hours`] * 60 || null,
        pay_hours: this.shift.hours[`${prefix}pay_hours`] * 60 || null,
        duration: this.shift.hours[`${prefix}duration`] * 60 || null
      }
    }
  }
}
</script>

<style scoped>
.wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}
</style>
